/**
 * This file produces a .js script (Finsemble's javascript-adapter) that can be loaded into a
 * browser page to gain access to Finsemble's API. This file is webpacked into a "UMD" file which
 * can be loaded into any browser page. `FSBLJSAdapter` and `fdc3` are added to the global namespace (window).
 * These represent the Finsemble API interface that is available to freestanding apps.
 *
 * The Finsemble API requires certain information in order to start (e.g. a windowName, router configuration, etc - type StartupParameters). This
 * information is provided in different ways depending on the "container" that Finsemble is running within. When
 * running in FEA, this information is obtained from information passed as "spawnData" (available on the `fin` object.)
 * When running in another JavaScript engine (e.g. in a browser) the FSBLJSAdapter object exposes a `startApp()` function that
 * developers must use to provide this information and start the API.
 */

import { Globals, FreestandingGlobals } from "../../common/Globals";
import { FSBLFreestanding } from "./FSBLFreestanding";
import { FinsembleDesktopAgent } from "../Interop/FinsembleDesktopAgent";
import { RouterClient } from "../routerClient";
import { Startup } from "../Startup/Startup";

if (Globals.FSBL) {
	console.warn(
		"finsemble-javascript-adapter.js - The JavaScript adapter should not be used within apps that are launched by Finsemble because it can cause conflicts with the FSBL.js preload."
	);
} else if (Globals.fin) {
	console.warn(
		"finsemble-javascript-adapter.js - It looks like the JavaScript adapter has been used in a preload. This could cause conflicts with the FSBL.js preload."
	);
}

/**
 * Starts the Finsemble API in freestanding mode. RouterClient and FinsembleDesktopAgent are both lazily instantiated so
 * this function will result in an inert API. The developer must call FSBLJSAdapter.startApp() in order to
 * initialize it.
 *
 * fdc3 is immediately ready for use (it employs queuing until connected).
 */
const main = () => {
	const fdc3 = new FinsembleDesktopAgent({
		routerClient: RouterClient,
		startup: Startup.getInstance(),
	});
	FreestandingGlobals.FSBL = {
		Clients: {
			RouterClient,
		},
	};
	FreestandingGlobals.fdc3 = fdc3;
	FreestandingGlobals.FSBLJSAdapter = new FSBLFreestanding({ fdc3 });
	/**
	 * Before this HTML window closes, tell the servers that we're disconnecting. This works except when browser
	 * windows crash, so it should eventually be supplemented with crash detection in the servers (that is smart
	 * enough to deal with temporary disconnect/reconnects).
	 */
	if (Globals.addEventListener)
		Globals.addEventListener("unload", () => {
			FreestandingGlobals.FSBLJSAdapter.stopApp();
		});
};

main();
